import React, { useRef } from 'react';

import {
  Circle,
  Container,
  HiddenInput,
  InputWrap,
  Legend,
  OptionLabel,
  Options,
  OptionText,
  RadioGroupSize,
} from './styles';

let radioGroupIdCounter = 0;

const createRadioGroupName = (): string => {
  radioGroupIdCounter += 1;

  return `radio-group-${radioGroupIdCounter}`;
};

export type RadioGroupOption = {
  value: string;
  label: React.ReactNode;
  disabled?: boolean;
};

export type RadioGroupProps = {
  name?: string;
  label?: React.ReactNode;
  value: string;
  options: RadioGroupOption[];
  onChange: (value: string) => void;
  size?: RadioGroupSize;
  color?: string;
  className?: string;
};

const RadioGroup = ({
  name,
  label,
  value,
  options,
  onChange,
  size = 'md',
  color,
  className,
}: RadioGroupProps): JSX.Element => {
  const generatedNameRef = useRef<string>();

  if (!generatedNameRef.current) {
    generatedNameRef.current = createRadioGroupName();
  }

  const groupName = name || generatedNameRef.current;

  return (
    <Container className={className}>
      {label && <Legend $color={color}>{label}</Legend>}
      <Options role="radiogroup" aria-label={typeof label === 'string' ? label : undefined}>
        {options.map((option) => {
          const optionId = `${groupName}-${option.value}`;

          return (
            <OptionLabel key={option.value} htmlFor={optionId}>
              <InputWrap>
                <HiddenInput
                  id={optionId}
                  type="radio"
                  name={groupName}
                  value={option.value}
                  checked={value === option.value}
                  disabled={option.disabled}
                  onChange={() => {
                    if (!option.disabled) onChange(option.value);
                  }}
                />
                <Circle $size={size} aria-hidden="true" />
              </InputWrap>
              <OptionText $color={color}>{option.label}</OptionText>
            </OptionLabel>
          );
        })}
      </Options>
    </Container>
  );
};

export default RadioGroup;
